/**
 * Phase 4.9 — Russian labels for job-state strings.
 *
 * The API returns raw English keys (``pending``, ``submitted``, ``done``,
 * ``failed``, ``cancelled``, …) because state is part of the wire
 * contract. The Mini App is operator-facing Russian, so the strings
 * get translated here on the render side — no API churn, no migration.
 *
 * The map covers the full ``JobState`` enum from
 * ``Code/task_db/types.py`` plus a few synthetic transition-only
 * keys the timeline can emit (``fetching``, ``validating``,
 * ``polling``, ``compute_failed``, ``substituted``). New states added
 * later get a verbatim fallback — see :func:`translateJobState`.
 *
 * Used by:
 *   * ``DetailScreen::renderEventText`` — turns ``state_change``
 *     event ``old → new`` into Russian.
 *
 * Anywhere else that displays a raw state string should funnel
 * through ``translateJobState``. Adding the next location is a
 * one-liner; the unknown-fallback keeps existing renders safe in
 * the meantime.
 */

const STATE_LABELS_RU: Record<string, string> = {
  pending: 'ожидает',
  fetching: 'скачивание RINEX',
  validating: 'валидация',
  submitted: 'отправлено на сервис',
  polling: 'опрос результата',
  done: 'готово',
  failed: 'ошибка',
  compute_failed: 'ошибка вычисления',
  cancelled: 'отменено',
  substituted: 'автозамена',
};

/**
 * Return the Russian label for a job-state key, or the raw input
 * verbatim if the key isn't in the map. The verbatim fallback is
 * deliberate — when a future phase adds a new state (e.g.
 * ``"reserved"``), the timeline doesn't render blank while we wait
 * for the translation to land; the operator sees the English key
 * and can flag it for the next UI pass.
 *
 * Case-insensitive lookup; the API consistently returns lower-case
 * keys but defensive normalisation costs nothing.
 */
export function translateJobState(state: string): string {
  return STATE_LABELS_RU[state.toLowerCase()] ?? state;
}
